import { Component, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { trigger, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { DepositModalService } from 'src/app/services/deposit-modal/deposit-modal.service';
import { DepositService } from 'src/app/services/deposit/deposit.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ClientAccountModalService } from 'src/app/services/client-account-modal/client-account-modal.service';
import { ClientService } from 'src/app/services/user/client.service';
import Swal from 'sweetalert2';

// Define filter interface
interface DepositFilters {
  keyword?: string;
  customer?: any;
  customer_id?: string;
  payment?: string;
  received_date_from?: string;
  received_date_to?: string;
}

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-manage-deposits-list',
  templateUrl: './manage-deposits-list.component.html',
  styleUrls: ['./manage-deposits-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ManageDepositsListComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  deposits: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;

  // Filter properties
  showFilters: boolean = false;
  filters: DepositFilters = {};

  // Customer search properties
  customerSearchTerm: string = '';
  selectedCustomer: any = null;
  customerSearchResults: any[] = [];
  showCustomerDropdown: boolean = false;
  isCustomerSearching: boolean = false;
  private customerSearchSubject = new Subject<string>();

  constructor(
    private _depositModalService: DepositModalService,
    private _depositService: DepositService,
    private _clientAccountModalService: ClientAccountModalService,
    private _clientService: ClientService,
    private router: Router
  ) {
    super();
    this.setupCustomerSearchDebounce();
  }

  ngOnInit(){
    this.GetData();
    this.setupModalSubscriptions();
  }

  private setupModalSubscriptions(): void {
    // Subscribe to deposit modal service changes if needed
    this.subscriptions.push(
      this._depositModalService.selectedDeposit$.subscribe(deposit => {
        // This subscription helps track modal state changes
        console.log('Modal deposit changed:', deposit);
      })
    );
  }

  ngAfterViewInit(): void {
    // Add event listeners for popup positioning
    setTimeout(() => {
    }, 100);

    // Initialize KTMenu instances
    this.initializeMenus();
  }

  // Helper method for tracking items in ngFor
  trackByDepositId(index: number, deposit: any): any {
    return deposit._id || index;
  }

  openAddDepositModal(): void {
    // Clear any selected deposit (for add mode)
    this._depositModalService.clearSelectedDeposit();

    // Open the modal
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditDepositModal(deposit: any): void {
    // Set the selected deposit for edit mode
    this._depositModalService.setSelectedDeposit(deposit);

    // Open the modal
    const modalElement = document.getElementById('kt_modal_add_update_deposit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onDepositAdded(): void {
    console.log('onDepositAdded called - Deposit added successfully');
    // Refresh the data
    this.GetData();
  }

  onDepositUpdated(): void {
    console.log('onDepositUpdated called - Deposit updated successfully');
    // Refresh the data
    this.GetData();
  }

  navigateToAllocation(customerId: string): void {
    this.router.navigate(['/sales/sales-payment/allocation', customerId]);
  }

  GetData(page: number = this.currentPage, search: string = this.searchTerm, filters: DepositFilters = this.filters){
    this.isLoading = true;
    this.currentPage = page;

    // Build filters object
    const apiFilters: any = {};

    if (filters.keyword) {
      apiFilters.keyword = filters.keyword;
    }
    if (filters.customer_id) {
      apiFilters.customer_id = filters.customer_id;
    }
    if (filters.payment && filters.payment !== 'All') {
      apiFilters.payment = filters.payment;
    }
    if (filters.received_date_from) {
      apiFilters.received_date_from = filters.received_date_from;
    }
    if (filters.received_date_to) {
      apiFilters.received_date_to = filters.received_date_to;
    }

    this.subscriptions.push(
      this._depositService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          console.log('API Response:', response); // Debug log
          if (response.status && response.data) {
            this.deposits = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            console.log('Deposits set:', this.deposits); // Debug log
            console.log('Pagination info:', this.paginationInfo); // Debug log
          } else {
            this.deposits = [];
            this.resetPagination();
            console.log('No data found in response'); // Debug log
          }
          this.isLoading = false;

          // Initialize menus after data loads
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading deposits:', error);
          this.deposits = [];
          this.resetPagination();
          this.isLoading = false;
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
        console.log('KTMenu instances initialized for deposits list');
      } else {
        console.warn('KTMenu is not available');
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
        console.log('KTMenu instances refreshed for deposits list');
      } else {
        console.warn('KTMenu is not available for refresh');
      }
    }, 50);
  }

  openClientAccountModal(customerId: string): void {
    console.log('Opening client account modal for customer:', customerId);

    if (!customerId) {
      console.error('No customer ID provided');
      return;
    }

    // Set the customer ID in the service
    this._clientAccountModalService.setSelectedCustomerId(customerId);

    // Small delay to ensure data is set
    setTimeout(() => {
      // Open the modal
      const modalElement = document.getElementById('kt_manage_deposits_client_account_modal');
      console.log('Modal element found:', modalElement);
      if (modalElement) {
        const modal = new bootstrap.Modal(modalElement);
        modal.show();
      }
    }, 100);
  }

  // Pagination method
  onPageChange(page: number): void {
    this.GetData(page, this.searchTerm, this.filters);
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    if (this.filters.customer) {
      this.filters.customer_id = this.filters.customer._id;
    }
    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.GetData();
  }

  resetFilters(): void {
    this.filters = {};
    this.searchTerm = '';
    this.clearCustomerSearch();
    this.showFilters = false;
    this.currentPage = 1;
    this.GetData();
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;
    this.filters.customer = null;
    this.filters.customer_id = undefined;
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  private searchCustomers(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.customerSearchResults = [];
      this.showCustomerDropdown = false;
      return;
    }

    this.isCustomerSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    this.customerSearchTerm = this.getCustomerDisplayName(customer);
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = customer;
    this.filters.customer_id = customer._id;
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];
    this.filters.customer = null;
    this.filters.customer_id = undefined;
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}, ${customer.currency_type}`;
    }
    return `${customer.name || ''} ${customer.last_name || ''}`.trim() || customer.email || 'Unknown Customer';
  }

  deleteDeposit(event: Event, deposit: any): void {
    event.stopPropagation(); // Prevent any parent click handlers

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete deposit "${deposit.reference_number}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(deposit);
      }
    });
  }

  private performDelete(deposit: any): void {
    const depositId = deposit._id || deposit.id;
    this.subscriptions.push(
      this._depositService.DeleteDataById(depositId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Deposit has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Reload the deposits list
              this.GetData();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete deposit.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting deposit.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

}


